import { useEffect, useState } from 'react';
import { Quest } from '@/network/RestApi';
import { useCharacterStore } from '@/state/characterStore';
import { useUIStore } from '@/state/uiStore';
import npcsData from '@/data/npcs.json';
import { ui } from './theme';
import { CloseButton } from './CloseButton';

const NPC_NAMES: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const n of (npcsData as { npcs: { id: string; name: string }[] }).npcs) {
    map[n.id] = n.name;
  }
  return map;
})();

interface Objective {
  type: string;
  npc?: string;
  monsterId?: number;
  level?: number;
  count: number;
}

interface Rewards {
  exp?: number;
  zen?: number;
  diamond?: number;
  items?: { tplId: number; qty: number }[];
}

interface QuestTpl {
  id: number;
  code: string;
  name: string;
  description?: string;
  type: string;
  levelReq: number;
  objectives: Objective[];
  rewards: Rewards;
}

interface QuestRow {
  id: number;
  questId: number;
  status: string;
  progress?: Record<string, number>;
}

function displayQuestName(name: string): string {
  return name.replace(/^[A-Z]{1,4}\d+[A-Za-z-]*\s*-\s*/, '');
}

function objectiveLabel(o: Objective): string {
  switch (o.type) {
    case 'talk': return `Nói chuyện với ${o.npc ? (NPC_NAMES[o.npc] ?? o.npc) : ''}`;
    case 'kill': return `Tiêu diệt quái #${o.monsterId}`;
    case 'collect_zen': return `Thu thập ${o.count} Zen`;
    case 'reach_level': return `Đạt level ${o.level}`;
    default: return o.type;
  }
}

function rewardLabel(r?: Rewards): string {
  if (!r) return '';
  const parts: string[] = [];
  if (r.exp) parts.push(`${r.exp} EXP`);
  if (r.zen) parts.push(`${r.zen} Zen`);
  if (r.diamond) parts.push(`${r.diamond} KC`);
  if (r.items?.length) parts.push(`${r.items.length} item`);
  return parts.join(', ');
}

export function QuestPanel() {
  const ch = useCharacterStore((s) => s.current);
  const close = () => useUIStore.getState().set('quest', false);
  const showToast = useUIStore((s) => s.showToast);
  const [list, setList] = useState<QuestRow[]>([]);
  const [tpls, setTpls] = useState<Record<number, QuestTpl>>({});
  const [selected, setSelected] = useState<number | null>(null);
  const [tab, setTab] = useState<'current' | 'all'>('current');

  const load = async () => {
    if (!ch) return;
    try {
      const [lr, tr] = await Promise.all([
        Quest.list(ch.id) as Promise<{ quests?: QuestRow[] }>,
        Quest.templates() as Promise<{ templates?: QuestTpl[] }>,
      ]);
      setList(lr.quests ?? []);
      const map: Record<number, QuestTpl> = {};
      (tr.templates ?? []).forEach((t) => { map[t.id] = t; });
      setTpls(map);
    } catch {
      setList([]);
    }
  };
  useEffect(() => { load(); }, [ch]);

  const ownedById = new Map(list.map((q) => [q.questId, q]));
  const activeList = list.filter((q) => q.status === 'accepted' || q.status === 'completed');
  const allTpls = Object.values(tpls).sort((a, b) => a.levelReq - b.levelReq || a.id - b.id);

  const renderDetail = (questId: number) => {
    if (selected !== questId) return null;
    const detail = tpls[questId];
    if (!detail) return null;
    const detailRow = list.find((q) => q.questId === questId);
    return (
      <div style={{ padding: 10, background: '#0b1220', borderRadius: 4, borderBottom: '1px solid #1f2933' }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{displayQuestName(detail.name)}</div>
        {detail.description && (
          <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 8 }}>{detail.description}</div>
        )}
        <div style={{ fontSize: 12, opacity: 0.7 }}>Loại: {detail.type} • Lv yêu cầu: {detail.levelReq}</div>
        <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600 }}>Mục tiêu</div>
        <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 12 }}>
          {(detail.objectives ?? []).map((o, i) => {
            const cur = detailRow?.progress?.[`obj_${i}`] ?? 0;
            const done = cur >= o.count;
            return (
              <li key={i} style={{ color: done ? '#66bb6a' : '#dce3ec' }}>
                {objectiveLabel(o)} ({cur}/{o.count})
              </li>
            );
          })}
        </ul>
        {detail.rewards && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#ffd166' }}>
            Thưởng: {rewardLabel(detail.rewards)}
          </div>
        )}
        {detailRow?.status === 'completed' && (
          <button
            style={{ ...ui.primaryBtn, marginTop: 10 }}
            onClick={async (e) => {
              e.stopPropagation();
              try {
                const res = await Quest.claim(ch!.id, { questId: detail.id }) as { ok: boolean; rewards: Rewards };
                const rew = res.rewards;
                const store = useCharacterStore.getState();
                if (rew.exp && store.current) {
                  const newExp = store.exp + rew.exp;
                  let newLevel = store.current.level;
                  let expToLevel = 100 * (newLevel + 1) * (newLevel + 1);
                  let leftover = newExp;
                  while (leftover >= expToLevel) {
                    leftover -= expToLevel;
                    newLevel++;
                    expToLevel = 100 * (newLevel + 1) * (newLevel + 1);
                  }
                  store.applyExpGain({ exp: leftover, level: newLevel });
                }
                if (rew.zen || rew.diamond) {
                  store.setCurrency({
                    zen: store.zen + (rew.zen ?? 0),
                    diamond: store.diamond + (rew.diamond ?? 0),
                  });
                }
                showToast('Nhận thưởng OK');
                load();
              } catch (err) { showToast((err as Error).message, 'error'); }
            }}
          >
            Nhận thưởng
          </button>
        )}
        {detailRow?.status === 'accepted' && (
          <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>
            Hoàn thành các mục tiêu trên để có thể nhận thưởng.
          </div>
        )}
        {!detailRow && (
          (ch?.level ?? 0) < detail.levelReq ? (
            <div style={{ marginTop: 8, fontSize: 12, color: '#ff6b6b' }}>
              Cần đạt Lv {detail.levelReq} để nhận nhiệm vụ này.
            </div>
          ) : (
            <button
              style={{ ...ui.primaryBtn, marginTop: 10 }}
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await Quest.accept(ch!.id, { questId: detail.id });
                  showToast('Đã nhận nhiệm vụ');
                  setTab('current');
                  load();
                } catch (err) { showToast((err as Error).message, 'error'); }
              }}
            >
              Nhận nhiệm vụ
            </button>
          )
        )}
      </div>
    );
  };

  const tabBtn = (key: 'current' | 'all', label: string) => (
    <button
      onClick={() => { setTab(key); setSelected(null); }}
      style={{
        flex: 1, padding: '6px 10px', cursor: 'pointer',
        background: tab === key ? '#162033' : 'transparent',
        color: tab === key ? '#ffd166' : '#dce3ec',
        border: 'none', borderBottom: tab === key ? '2px solid #ffd166' : '2px solid #1f2933',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ position: 'absolute', top: 70, right: 340, width: 420, ...ui.panel }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong>Nhiệm vụ</strong>
        <CloseButton onClick={close} />
      </div>
      <div style={{ display: 'flex', marginBottom: 8 }}>
        {tabBtn('current', `Đang làm (${activeList.length})`)}
        {tabBtn('all', `Tất cả (${allTpls.length})`)}
      </div>

      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
      {tab === 'current' && activeList.length === 0 && (
        <div style={{ opacity: 0.7 }}>Chưa có nhiệm vụ. Mở tab "Tất cả" hoặc tìm NPC có dấu [Quest].</div>
      )}
      {tab === 'current' && activeList.map((q) => {
        const t = tpls[q.questId];
        const isSel = selected === q.questId;
        return (
          <div key={q.id}>
            <div
              onClick={() => setSelected(isSel ? null : q.questId)}
              style={{
                padding: 8, borderBottom: '1px solid #1f2933', cursor: 'pointer',
                background: isSel ? '#162033' : 'transparent',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{t?.name ? displayQuestName(t.name) : `Quest #${q.questId}`}</span>
                <span style={{ fontSize: 12, opacity: 0.7 }}>{q.status}</span>
              </div>
            </div>
            {renderDetail(q.questId)}
          </div>
        );
      })}

      {tab === 'all' && allTpls.length === 0 && (
        <div style={{ opacity: 0.7 }}>Không có dữ liệu nhiệm vụ.</div>
      )}
      {tab === 'all' && allTpls.map((t) => {
        const owned = ownedById.get(t.id);
        const isSel = selected === t.id;
        const status = owned?.status ?? 'available';
        const charLv = ch?.level ?? 0;
        const locked = charLv < t.levelReq;
        return (
          <div key={t.id}>
            <div
              onClick={() => setSelected(isSel ? null : t.id)}
              style={{
                padding: 8, borderBottom: '1px solid #1f2933', cursor: 'pointer',
                background: isSel ? '#162033' : 'transparent',
                opacity: locked && status === 'available' ? 0.6 : 1,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{displayQuestName(t.name)} <span style={{ fontSize: 11, opacity: 0.6 }}>Lv{t.levelReq}</span></span>
                <span style={{ fontSize: 12, opacity: 0.7 }}>{status}</span>
              </div>
            </div>
            {renderDetail(t.id)}
          </div>
        );
      })}
      </div>
    </div>
  );
}
